'use strict';

// external imports
import React, {
    MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {confirmPopup} from 'primereact/confirmpopup';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import Item from '~/shared/src/ci/Item';
import {getXmlForContext} from '~/shared/src/utilities/xmlExport';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {LoadItemResult} from '~/shared/src/utilities/modules/data';

// frontend imports
import {Mode as FormDesignerMode} from '~/frontend/src/components/base/FormDesigner';
import FormDesignerView from '~/frontend/src/modules/panels/FormDesignerView';

import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    getDataRecursive,
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {setMessage} from '~/frontend/src/actions/intercom';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import deleteForm from '~/frontend/src/actions/panel/formDesigner/delete';
import showDeleteSuccessDialog from '~/frontend/src/actions/panelManager/showDeleteSuccessDialog';


export type DefaultProps = {

    /**
     * The mode of the formDesigner
     */
    mode? : FormDesignerMode;

    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton? : boolean;

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount? : number;

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip? : React.ReactNode;

    saveButtonDisabled? : boolean;

    /**
     * Should the clone Button shown in the toolbar <strong>or</strong> not.
     */
    showCloneButton? : boolean;

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton? : boolean;

    deleteButtonDisabled? : boolean;

    /**
     * Show copy XML button.
     */
    showCopyXmlButton? : boolean;

    /**
     * Show download XML button.
     */
    showDownloadXmlButton? : boolean;

    showPanelSelectorDropdown? : boolean;

    /**
     * Show reload button.
     */
    showReloadButton? : boolean;

    /**
     * Show mode selection buttons
     */
    showModeSelector? : boolean;

    /**
     * Show data rootItem dropdown
     */
    showDataRootItemSelector? : boolean;

    showNewLayoutElementsWidth? : boolean;
    newLayoutElementsWidth? : string;

    showHiddenLayoutElementsCheckbox? : boolean;
    showHiddenLayoutElements? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    mode                            : 'design',
    newLayoutElementsWidth          : '100%',
    saveButtonErrorCount            : 0,
    saveButtonErrorTooltip          : '',
    saveButtonDisabled              : false,
    showCloneButton                 : true,
    showCopyXmlButton               : true,
    deleteButtonDisabled            : false,
    showDataRootItemSelector        : true,
    showDeleteButton                : true,
    showDownloadXmlButton           : true,
    showHiddenLayoutElements        : false,
    showHiddenLayoutElementsCheckbox: true,
    showModeSelector                : true,
    showNewLayoutElementsWidth      : true,
    showPanelSelectorDropdown       : true,
    showReloadButton                : true,
    showSaveButton                  : true
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The id of the root item
     */
    rootItemId : number;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    dataContext : string;

    treeViewItem : Item;

    dataRootItemId? : number;
    dataItemType? : string;
    dataSchema? : Schema;

    /**
     * Event for reload button
     */
    onReloadButtonClick? : () => void;

    onDataRootItemIdChange? : ( dataRootItemId : number ) => void;

    onNewLayoutElementsWidthChange? : ( newLayoutElementsWidth : string ) => void;

    onShowHiddenLayoutElementsChange? : ( showHiddenLayoutElements : boolean ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const dispatch : AppDispatch                   = useDispatch(),
          {
              dataContext,
              dataItemType,
              dataRootItemId,
              dataSchema,
              deleteButtonDisabled,
              mode,
              newLayoutElementsWidth,
              onDataRootItemIdChange,
              onNewLayoutElementsWidthChange,
              onShowHiddenLayoutElementsChange,
              rootItemId,
              saveButtonDisabled,
              saveButtonErrorCount,
              saveButtonErrorTooltip,
              schema,
              showCloneButton,
              showCopyXmlButton,
              showDataRootItemSelector,
              showDeleteButton,
              showDownloadXmlButton,
              showHiddenLayoutElements,
              showHiddenLayoutElementsCheckbox,
              showModeSelector,
              showNewLayoutElementsWidth,
              showPanelSelectorDropdown,
              showReloadButton,
              showSaveButton,
              treeViewItem,
              uid
          }                                        = props,
          user                                     = useSelector( ( state : StoreState ) => state.session?.user ),
          elusiveDataData                          = useSelector( ( state : StoreState ) => uid ? state.elusiveData?.[ uid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
          loading                                  = useRef<boolean>( false ),
          [ dataRootItem, setDataRootItem ]        = useState<Item | undefined>( undefined ),
          language                                 = user
              ? user.language
              : undefined,
          globalVersions                           = useMemo(
              () => {
                  return JSON.stringify( elusiveDataData ?? {} );
              },
              [
                  elusiveDataData
              ]
          ),
          data                                     = useMemo(
              () => {
                  return getDataRecursive( dataContext, 'CFG', rootItemId );
              },
              [
                  dataContext,
                  globalVersions,
                  rootItemId
              ]
          ),
          itemIds                                  = useMemo(
              () => {
                  return data.items.map( item => item.id );
              },
              [
                  data
              ]
          ),
          referenceIds                             = useMemo(
              () => {
                  return data.references.map( reference => reference.id );
              },
              [
                  data
              ]
          ),
          onSaveButtonClick                        = useCallback(
              () => {
                  void FormDesignerView.doSaveData( uid, dispatch );
              },
              [
                  uid
              ]
          ),
          onCloneButtonClick                       = useCallback(
              ( event : MouseEvent<HTMLButtonElement> ) => {
                  confirmPopup( {
                      target         : event.currentTarget,
                      message        : 'Not implemnted yet!',
                      className      : 'yn-confirm-popup-notimplemented',
                      icon           : 'pi pi-exclamation-triangle yn-warning',
                      acceptLabel    : __( 'YN.base.common.yes' ),
                      acceptClassName: 'p-button p-button-outlined',
                      rejectLabel    : __( 'YN.base.common.no' ),
                      rejectClassName: 'p-button-secondary p-button-outlined mr-3'
                  } );
              },
              [
                  language
              ]
          ),
          onDeleteButtonClick                      = useCallback(
              () => {
                  void dispatch( deleteForm( uid, rootItemId, () => {
                      dispatch( showDeleteSuccessDialog( uid ) );
                  } ) );
              },
              [
                  uid,
                  rootItemId
              ]
          ),
          onCopyXmlApply                           = useCallback(
              async () => {
                  await navigator.clipboard.writeText( getXmlForContext( dataContext, itemIds, referenceIds, true, true ) );

                  dispatch( setMessage(
                      uid,
                      'toast',
                      'xmlDataCopiedToClipboard',
                      {
                          severity: 'info',
                          summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                          detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
                      }
                  ) );
              },
              [
                  dataContext,
                  language,
                  itemIds,
                  referenceIds,
                  uid
              ]
          ),
          onDownloadXmlApply                       = useCallback(
              () => {
                  fileDownload(
                      getXmlForContext( dataContext, itemIds, referenceIds, true, true ),
                      `ViewDesigner_${treeViewItem.key}`,
                      'xml'
                  );
              },
              [
                  dataContext,
                  language,
                  itemIds,
                  referenceIds,
                  treeViewItem
              ]
          ),
          onReloadButtonClick                      = () => {
              props.onReloadButtonClick?.();
          },
          onModeSelectionButtonClick               = useCallback(
              ( modeToSet : AllProps[ 'mode' ] ) => {
                  dispatch( setParam( uid, 'mode', modeToSet ) );
              },
              [
                  uid
              ]
          ),
          onSearchItemDropdownChange               = useCallback(
              ( item : Item ) => {
                  setDataRootItem( item );
                  onDataRootItemIdChange?.( item?.id );
              },
              [
                  onDataRootItemIdChange
              ]
          ),
          onNewLayouteElementsWidthButtonClick     = useCallback(
              ( newLayoutElementsWidthToSet : string ) => {
                  onNewLayoutElementsWidthChange?.( newLayoutElementsWidthToSet );
              },
              [
                  onNewLayoutElementsWidthChange
              ]
          ),
          onShowHiddenLayoutElementsCheckboxChange = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  onShowHiddenLayoutElementsChange?.( event.checked! );
              },
              [
                  onShowHiddenLayoutElementsChange
              ]
          );

    // load data rootItem
    useEffect(
        () => {
            if ( !loading.current && dataRootItemId && dataRootItem?.id !== dataRootItemId ) {
                const doLoad = async () => {
                    const response = await xhr.post(
                        `${BASE_URL}/api/data/loadItem`,
                        {
                            schema: dataSchema,
                            id    : dataRootItemId
                        }
                    );

                    if ( response?.data ) {
                        const responseData = response.data as LoadItemResult;

                        if ( responseData.success ) {
                            setDataRootItem( new Item( responseData.item, undefined, dataSchema, true ) );
                        }
                    }

                    loading.current = false;
                };

                loading.current = true;
                void doLoad();
            }
        }
    );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showSaveButton ) {
                layoutComponents.left.push( {
                    type  : 'saveButton',
                    saveButtonErrorCount,
                    saveButtonErrorTooltip,
                    params: {
                        key: `${uid}_saveButton`,

                        disabled: saveButtonDisabled,
                        onClick : onSaveButtonClick
                    }
                } );
            }

            if ( showCloneButton ) {
                layoutComponents.left.push( {
                    type  : 'cloneButton',
                    params: {
                        key: `${uid}_cloneButton`,

                        onClick: onCloneButtonClick
                    }
                } );
            }

            if ( showDeleteButton ) {
                layoutComponents.left.push( {
                    type  : 'deleteButton',
                    params: {
                        key: `${uid}_deleteButton`,

                        disabled: deleteButtonDisabled,
                        confirm : true,
                        onClick : onDeleteButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showCopyXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'copyXml',
                    params: {
                        key: `${uid}_copyXmlButton`,

                        uid,

                        onExpand: () => {
                            void onCopyXmlApply();
                            return false;
                        },
                        onApply : () => {
                            void onCopyXmlApply();
                        }
                    }
                } );
            }

            if ( showDownloadXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'downloadXml',
                    params: {
                        key: `${uid}_downloadXmlButton`,

                        uid,

                        onExpand: () => {
                            onDownloadXmlApply();
                            return false;
                        },
                        onApply : onDownloadXmlApply
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showModeSelector ) {
                layoutComponents.left.push( {
                    type  : 'buttonSet',
                    params: {
                        key: `${uid}_modeSelectionButtons`,

                        buttons: [
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_designModeButton`,

                                    label    : __( 'YN.base.frontend.formDesigner.mode.design.label' ),
                                    onClick  : onModeSelectionButtonClick.bind( null, 'design' ),
                                    className: mode === 'design' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_inputPanelButton`,

                                    label    : __( 'YN.base.frontend.formDesigner.mode.input.label' ),
                                    onClick  : onModeSelectionButtonClick.bind( null, 'input' ),
                                    className: mode === 'input' ? '' : 'p-button-outlined'
                                }
                            }
                        ]
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showDataRootItemSelector ) {
                layoutComponents.left.push( {
                    type  : 'searchItemDropdown',
                    params: {
                        key: `${uid}_dataRootItemDropdown`,

                        uid,

                        schema      : dataSchema ?? 'DATA' as Schema,
                        status      : 'active',
                        itemTypeName: dataItemType ?? '',
                        disabled    : !dataSchema || !dataItemType,
                        value       : dataRootItem,
                        onChange    : onSearchItemDropdownChange,
                        label       : __( 'YN.base.frontend.formDesigner.dataRootItem.label' ),
                        inputStyle  : {
                            width: '18rem'
                        }
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showNewLayoutElementsWidth ) {
                layoutComponents.left.push( {
                    type  : 'buttonSet',
                    params: {
                        key: `${uid}_newLayoutElementsWidthButtons`,

                        label: __( 'YN.base.frontend.formDesigner.layoutElementsWidth.label' ),

                        buttons: [
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_25_button`,

                                    label    : __( 'YN.base.frontend.formDesigner.layoutElementsWidth.25.label' ),
                                    onClick  : onNewLayouteElementsWidthButtonClick.bind( null, '25%' ),
                                    className: newLayoutElementsWidth === '25%' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_33_button`,

                                    label    : __( 'YN.base.frontend.formDesigner.layoutElementsWidth.33.label' ),
                                    onClick  : onNewLayouteElementsWidthButtonClick.bind( null, '33%' ),
                                    className: newLayoutElementsWidth === '33%' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_50_button`,

                                    label    : __( 'YN.base.frontend.formDesigner.layoutElementsWidth.50.label' ),
                                    onClick  : onNewLayouteElementsWidthButtonClick.bind( null, '50%' ),
                                    className: newLayoutElementsWidth === '50%' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_66_button`,

                                    label    : __( 'YN.base.frontend.formDesigner.layoutElementsWidth.66.label' ),
                                    onClick  : onNewLayouteElementsWidthButtonClick.bind( null, '66%' ),
                                    className: newLayoutElementsWidth === '66%' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_75_button`,

                                    label    : __( 'YN.base.frontend.formDesigner.layoutElementsWidth.75.label' ),
                                    onClick  : onNewLayouteElementsWidthButtonClick.bind( null, '75%' ),
                                    className: newLayoutElementsWidth === '75%' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_100_button`,

                                    label    : __( 'YN.base.frontend.formDesigner.layoutElementsWidth.100.label' ),
                                    onClick  : onNewLayouteElementsWidthButtonClick.bind( null, '100%' ),
                                    className: newLayoutElementsWidth === '100%' ? '' : 'p-button-outlined'
                                }
                            }
                        ]
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showHiddenLayoutElementsCheckbox ) {
                layoutComponents.left.push( {
                    type: 'custom',

                    params: {
                        key      : `${uid}_showHiddenLayoutElementsCheckbox`,
                        className: 'flex align-items-center field',
                        component: <>
                            <label
                                htmlFor={`${uid}_showHiddenLayoutElementsCheckbox`}
                                className="block"
                            >
                                {__( 'YN.base.frontend.formDesigner.showHiddenLayoutElements.label' )}
                            </label>
                            <Checkbox
                                checked={showHiddenLayoutElements}
                                onChange={onShowHiddenLayoutElementsCheckboxChange}
                                style={{ flex: '0 0 auto', width: 'auto' }}
                            />
                        </>
                    }
                } );
            }

            // right
            if ( showPanelSelectorDropdown ) {
                const item       = rootItemId
                          ? global.data[ dataContext ]?.items[ rootItemId ]
                          : undefined,
                      itemParams = item
                          ? {
                              schema  : item.schema,
                              itemId  : item.id,
                              itemType: item.itemTypeId
                          }
                          : {};

                layoutComponents.right.push( {
                    type  : 'panelSelectorDropdown',
                    params: {
                        key: `${uid}_panelSelector`,

                        uid        : `${uid}_panelSelector`,
                        labeled    : true,
                        style      : { width: '18rem' },
                        currentView: `FormDesignerView`,
                        ...itemParams
                    }
                } );
            }

            layoutComponents.right.push( {
                type: 'separator'
            } );

            if ( showReloadButton ) {
                layoutComponents.right.push( {
                    type  : 'reloadButton',
                    params: {
                        key: `${uid}_reloadButton`,

                        confirm: () => rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema! ),
                        onClick: onReloadButtonClick
                    }
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />;
        },
        [
            dataItemType,
            dataRootItem,
            dataRootItemId,
            dataSchema,
            language,
            mode,
            newLayoutElementsWidth,
            onCloneButtonClick,
            onCopyXmlApply,
            onDeleteButtonClick,
            onDownloadXmlApply,
            onModeSelectionButtonClick,
            onNewLayouteElementsWidthButtonClick,
            onSaveButtonClick,
            onSearchItemDropdownChange,
            onShowHiddenLayoutElementsCheckboxChange,
            saveButtonErrorCount,
            saveButtonErrorTooltip,
            showCloneButton,
            showCopyXmlButton,
            showDataRootItemSelector,
            showDeleteButton,
            showDownloadXmlButton,
            showHiddenLayoutElements,
            showHiddenLayoutElementsCheckbox,
            showModeSelector,
            showNewLayoutElementsWidth,
            showReloadButton,
            showSaveButton,
            uid
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'FormDesigner-Toolbar';

export default Toolbar;
